import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { dataSource, type DataTypeSortOption } from "@/lib/dataSource";
import { Type, Code, TypeEdge, AuditEntry, Settings } from "../shared/schema";


// Authentication
export function useAuthMe() {
  return useQuery({
    queryKey: ["authmetypes"],
    queryFn: () => dataSource.authMe(),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
  });
}

export function useRefresh() {
  return useQuery({
    queryKey: ["refreshtypes"],
    queryFn: () => dataSource.refresh(),
    enabled: false, // Don't auto-fetch, only call manually
    retry: false,
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => dataSource.logout(),
    onSettled: () => {
      window.location.href = "https://bprm-ai-portal.workfinity.com/login";
    },
  });
}

//Query APIs
export function useTypes(parentType: String, parentCode: number) {
  return useQuery({
    queryKey: ["types", parentType, parentCode],
    queryFn: () => dataSource.listTypes(parentType, parentCode),
  });
}

export function useAllTypes() {
  return useQuery({
    queryKey: ["alltypes"],
    queryFn: () => dataSource.listAllTypes(),
  });
}

export function useDataTypes(pageIndex: number, pageSize?: number, typeKey?: string) {
  return useQuery({
    queryKey: ["datatypes", pageIndex, pageSize, typeKey],
    queryFn: () => dataSource.listDataTypes(pageIndex, pageSize, typeKey),
  });
}

export function useAttributes(parentCode: string) {
  return useQuery({
    queryKey: ["getattribute", parentCode],
    queryFn: () => dataSource.listAttributes(parentCode),
  });
}

export function useInfiniteFilterDataTypes(
  searchTerm: string = "",
  pageSize: number = 10,
  sort: DataTypeSortOption = "Standard",
  enabled: boolean = true
) {
  return useInfiniteQuery({
    queryKey: ["filterCatalogDataTypes", searchTerm, pageSize, sort],
    queryFn: ({ pageParam = 1 }) =>
      dataSource.filterCatalogDataTypes(searchTerm, pageParam as number, pageSize, sort),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const items = lastPage?.items ?? [];
      if (items.length < pageSize) {
        return undefined;
      }
      const total = lastPage?.total;
      if (typeof total === "number") {
        const totalFetched = allPages.reduce((acc, p) => acc + (p?.items?.length ?? 0), 0);
        if (totalFetched >= total) {
          return undefined;
        }
      }
      const currentPage = (lastPage?.page as number) ?? allPages.length;
      return currentPage + 1;
    },
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useGetFilterType(filterName?: string, filterType?: string) {
  return useQuery({
    queryKey: ["typess", filterName, filterType],
    queryFn: () => dataSource.listFilterType(filterName ?? "", filterType ?? ""),
    placeholderData: keepPreviousData,
  });
}

export function useType(key: string) {
  return useQuery({
    queryKey: ["typess", key],
    queryFn: () => dataSource.getType(key),
    enabled: !!key,
  });
}

export function useCodes(
  typeFilter?: string,
  parentTypeFilter?: string,
  parentCodeFilter?: string
) {
  return useQuery({
    queryKey: ["codes", typeFilter, parentTypeFilter, parentCodeFilter],
    queryFn: () =>
      dataSource.listCodes(typeFilter, parentTypeFilter, parentCodeFilter),
  });
}

export function useCode(type: string, code: string) {
  return useQuery({
    queryKey: ["codes", type, code],
    queryFn: () => dataSource.getCode(type, code),
    enabled: !!type && !!code,
  });
}

export function useTypeEdges() {
  return useQuery({
    queryKey: ["typeEdges"],
    queryFn: () => dataSource.listTypeEdges(),
  });
}

export function useSearch(query: string) {
  return useQuery({
    queryKey: ["search", query],
    queryFn: () => dataSource.search(query),
    enabled: query.length >= 2,
  });
}

export function useDataQuality() {
  return useQuery({
    queryKey: ["dataQuality"],
    queryFn: () => dataSource.analyzeDataQuality(),
  });
}

export function useAuditLog(filters?: any) {
  return useQuery({
    queryKey: ["auditLog", filters],
    queryFn: () => dataSource.getAuditLog(filters),
  });
}

export function useStats() {
  return useQuery({
    queryKey: ["stats"],
    queryFn: () => dataSource.getStats(),
  });
}

// AI Assistant
export function useChatWithAI() {
  return useMutation({
    mutationFn: ({ message, sessionId, microservice = "datamanager", page = 1 }: { message: string; sessionId: string; microservice?: string; page?: number }) =>
      dataSource.chatWithAI(message, sessionId, microservice, page),
  });
}

// Mutations
export function useCreateDataType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dataSource.createDataType.bind(dataSource),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["datatypes"], exact: false });
    },
  });
}

export function useUpdateDataType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, updates }: { key: number; updates: Partial<Type> }) =>
      dataSource.updateDataType(key, updates),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: ["datatypes"] });
      // queryClient.invalidateQueries({ queryKey: ["types", key] });
      // queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useDeleteDataType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, force }: { key: string; force?: boolean; }) =>
      dataSource.deleteDataType(key, force),
    onMutate: async ({ key }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["getattribute"] }),
        queryClient.cancelQueries({ queryKey: ["datatypes"] }),
      ]);
      const previousTypes = queryClient.getQueryData<any>(["getattribute"]);
      const previousCodes = queryClient.getQueryData<any>(["datatypes"]);
      if (previousTypes) {
        queryClient.setQueryData(["datatypes"], (old: any) =>
          (old || []).filter((t: any) => t.key !== key)
        );
      }
      if (previousCodes) {
        queryClient.setQueryData(["getattribute"], (old: any) =>
          (old || []).filter((c: any) => c.type !== key)
        );
      }
      return { previousTypes, previousCodes } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      if (ctx.previousTypes)
        queryClient.setQueryData(["datatypes"], ctx.previousTypes);
      if (ctx.previousCodes)
        queryClient.setQueryData(["getattribute"], ctx.previousCodes);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["datatypes"] });
      queryClient.invalidateQueries({ queryKey: ["getattribute"] });
      // queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useCreateAttribute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id, data }: { Id: string; data: Partial<Code> }) =>
      dataSource.createAttribute(Id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["datatypes"] });
      queryClient.invalidateQueries({ queryKey: ["getattribute"] });
    },
  });
}

export function useUpdateAttribute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      type,
      code,
      updates,
    }: {
      type: string;
      code: string;
      updates: Partial<Code>;
    }) => dataSource.updateAttribute(type, code, updates),
    onSuccess: (_, { type, code }) => {
      queryClient.invalidateQueries({ queryKey: ["datatypes"] });
      // queryClient.invalidateQueries({ queryKey: ["codes", type, code] });
      queryClient.invalidateQueries({ queryKey: ["getattribute"] });
    },
  });
}

export function useDeleteAttribute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      type,
      code,
      force,
    }: {
      type: string;
      code: string;
      force?: boolean;
    }) => dataSource.deleteAttribute(type, code, force),
    onMutate: async ({ type, code }) => {
      await queryClient.cancelQueries({ queryKey: ["getattribute"] });
      const previousCodes = queryClient.getQueryData<any>(["getattribute"]);
      if (previousCodes) {
        queryClient.setQueryData(["getattribute"], (old: any) =>
          (old || []).filter((c: any) => !(c.type === type && c.code === code))
        );
      }
      return { previousCodes } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousCodes)
        queryClient.setQueryData(["getattribute"], ctx.previousCodes);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["getattribute"] });
      queryClient.invalidateQueries({ queryKey: ["datatypes"] });
    },
  });
}
